import {FC, ReactNode} from 'react';
import {Controller, RegisterOptions, useFormContext} from 'react-hook-form';
import Field from 'components/Form/Field';
import {RequiredType} from 'components/Form/types';
import {getRegisterRules} from 'components/Form/utils';
import useFormError from 'hooks/useFormErrors';
import BaseDropdownSelect, {BaseDropdownProps} from './BaseDropdownSelect';

type DropdownProps = BaseDropdownProps & {
    className?: string;
    helpText?: ReactNode;
    label?: ReactNode;
    name: string;
    required?: boolean;
    requiredType?: RequiredType;
    rules?: RegisterOptions;
};

const DropdownSelect: FC<DropdownProps> = ({
    className,
    disabled,
    error,
    helpText,
    isMultiChoices,
    label,
    name,
    onChange,
    options,
    required,
    requiredType,
    rules,
    ...rest
}) => {
    const {control, register, watch} = useFormContext<{
        [k: string]: string | string[];
    }>();

    const registerRules = getRegisterRules({disabled, required, rules});

    const {ref, ...registration} = register(name, registerRules);

    const value = watch(name);

    const fieldError = useFormError(name, error);

    return (
        <Field
            className={className}
            disabled={registerRules?.disabled}
            error={fieldError}
            helpText={helpText}
            label={label}
            name={name}
            required={!!registerRules?.required}
            requiredType={requiredType}
            type="select"
        >
            <Controller
                control={control}
                render={({field: {onChange: onFieldChange}}) => (
                    //@ts-ignore
                    <BaseDropdownSelect
                        disabled={registerRules?.disabled}
                        error={fieldError}
                        isMultiChoices={isMultiChoices}
                        options={options}
                        value={value}
                        {...rest}
                        onChange={(newValue: unknown) => {
                            onFieldChange(newValue);
                            if (onChange)
                                onChange(
                                    newValue as (string & string[]) | undefined,
                                );
                        }}
                    />
                )}
                {...registration}
            />
        </Field>
    );
};

export default DropdownSelect;
